import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { Loader } from 'components/Loader';

import {
  fetchLocations,
  LocationsState,
} from '../../../../store/slices/locationsSlice';
import { UsersState } from '../../../../store/slices/usersSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { Location } from '../../../../types/locationTypes';

interface SelectLocationModalProps {
  open: boolean;
  onClose: () => void;
  handleLocationSelect: (locationId: number) => void;
}

const SelectLocationModal = ({
  open,
  onClose,
  handleLocationSelect,
}: SelectLocationModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const selectedUserLocations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['users', 'selectedUserLocations']),
  ) as UsersState['selectedUserLocations'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (orgId && locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }

        if (globalLocationId === -1) {
          if (selectedUserLocations && selectedUserLocations.length > 0) {
            // Default to the user's default location
            setSelectedLocation(selectedUserLocations[0].id);
          } else if (locations.length > 0) {
            // Default to the first available location
            setSelectedLocation(locations[0].id);
          }
        } else {
          setSelectedLocation(globalLocationId || null); // Use globalLocationId if it's valid
        }
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, orgId, selectedUserLocations, locations, globalLocationId]);

  const handleOk = () => {
    if (selectedLocation !== null) {
      handleLocationSelect(selectedLocation); // Execute location select on OK
    }
    onClose();
  };

  return (
    <OpModal
      title={t('selectLocationModal.title')}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      centered
      width={720}
      footer={[
        <OpButton key="cancel" onClick={onClose}>
          {t('selectLocationModal.cancel')}
        </OpButton>,
        <OpButton key="ok" type="primary" onClick={handleOk}>
          {t('selectLocationModal.ok')}
        </OpButton>,
      ]}
    >
      <OpSpace
        orientation="vertical"
        size="large"
        style={{ display: 'flex', paddingTop: '16px' }}
      >
        {loading ? (
          <Loader />
        ) : (
          <OpSelect
            value={
              selectedLocation !== null ? String(selectedLocation) : undefined
            }
            options={locations.map((location: Location) => ({
              label: location.name.toString(),
              value: String(location.id),
            }))}
            onChange={(value) => setSelectedLocation(Number(value))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            loading={loading}
            style={{ width: '100%' }}
          />
        )}
      </OpSpace>
    </OpModal>
  );
};

export default SelectLocationModal;
